import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Languages, Snail, Volume2, X } from 'lucide-react';
import { trpc } from '@org/clients/school';

/**
 * Class page "select to translate / listen". Inside the lesson text
 * (cover, block titles, lesson text and exercises), selecting text shows a small ink pill with "Listen" and
 * "Translate"; tapping a single word opens its translation straight away.
 * The translation opens in a bottom sheet (the phone's own copy/paste menu
 * sits above a selection and cannot be removed, so nothing floats there on
 * touch screens). Listening uses the browser's English voice; when the
 * browser has none, the listen buttons are hidden.
 *
 * Rendered into <body> so the sheet sits above the page's floating buttons
 * (the lesson area is its own stacking context); `.z-sel-root` therefore
 * carries the zine colour and font tokens itself.
 */

const MAX_SELECTION_CHARS = 400;
/** "Slow" listening speed (1 = normal). */
const SLOW_RATE = 0.5;
const WORD_CHAR = /[A-Za-z’'-]/;
/** Tapping these does their own job (answering, opening a link…), never a translation. */
const NO_TAP_TARGETS =
  'a, button, input, textarea, select, label, img, video, audio, [contenteditable], [draggable="true"], ' +
  '[role="button"], [role="option"], [role="radio"], [role="checkbox"], [role="tab"], [role="slider"], [role="dialog"]';
/** Text the student types is hers, not lesson text. */
const NO_SELECTION_TARGETS = 'input, textarea, select, [contenteditable]';

type Target = { text: string; context: string; rect: DOMRect };

export function SelectionHelper({ containerRef }: { containerRef: RefObject<HTMLElement> }) {
  const { t } = useTranslation('p2e');
  const [pill, setPill] = useState<Target | null>(null);
  const [sheet, setSheet] = useState<Target | null>(null);
  const voice = useEnglishVoice();
  const speech = useSpeech(voice);

  const close = useCallback(() => {
    setPill(null);
    setSheet(null);
    speech.stop();
    // Otherwise tapping the same word again lands on a live selection and is ignored.
    window.getSelection()?.removeAllRanges();
  }, [speech]);

  const openSheet = useCallback(
    (target: Target) => {
      setPill(null);
      setSheet(target);
      // Keep the word visible above the sheet.
      const limit = window.innerHeight * 0.45;
      if (target.rect.bottom > limit) {
        window.scrollBy({ top: target.rect.bottom - limit + 24, behavior: 'smooth' });
      }
    },
    [],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const readSelection = () => {
      const target = selectionTarget(container);
      if (target) setPill(target);
    };
    const onPointerUp = (event: PointerEvent) => {
      clearTimeout(timer);
      // Touch selection keeps changing for a moment after the finger lifts.
      timer = setTimeout(readSelection, event.pointerType === 'touch' ? 350 : 10);
    };
    const onClick = (event: MouseEvent) => {
      if (event.detail > 1) return;
      const element = event.target as Element | null;
      if (!element || !container.contains(element) || element.closest(NO_TAP_TARGETS)) return;
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) return;
      const target = wordTargetAt(event.clientX, event.clientY, container);
      if (target) openSheet(target);
    };

    container.addEventListener('pointerup', onPointerUp);
    container.addEventListener('click', onClick);
    return () => {
      clearTimeout(timer);
      container.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('click', onClick);
    };
  }, [containerRef, openSheet]);

  useEffect(() => {
    if (!pill && !sheet) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const onSelectionChange = () => {
      const selection = window.getSelection();
      if (pill && (!selection || selection.isCollapsed)) setPill(null);
    };
    const onScroll = () => setPill(null);
    document.addEventListener('keydown', onKey);
    document.addEventListener('selectionchange', onSelectionChange);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('selectionchange', onSelectionChange);
      window.removeEventListener('scroll', onScroll);
    };
  }, [pill, sheet, close]);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="z-sel-root">
      <style dangerouslySetInnerHTML={{ __html: SELECTION_HELPER_CSS }} />
      {pill && !sheet && (
        <SelectionPill
          target={pill}
          canSpeak={!!voice}
          speaking={speech.speaking}
          onListen={() => speech.speak(pill.text, 0.95)}
          onTranslate={() => openSheet(pill)}
          labels={{
            toolbar: t('selectionHelp.toolbarLabel'),
            listen: t('selectionHelp.listen'),
            translate: t('selectionHelp.translate'),
          }}
        />
      )}
      {sheet && (
        <TranslationSheet
          target={sheet}
          canSpeak={!!voice}
          speakingRate={speech.speaking}
          onSpeak={(rate) => speech.speak(sheet.text, rate)}
          onClose={close}
        />
      )}
    </div>,
    document.body,
  );
}

function SelectionPill({
  target,
  canSpeak,
  speaking,
  onListen,
  onTranslate,
  labels,
}: {
  target: Target;
  canSpeak: boolean;
  speaking: number | null;
  onListen: () => void;
  onTranslate: () => void;
  labels: { toolbar: string; listen: string; translate: string };
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number; below: boolean } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { rect } = target;
    const width = el.offsetWidth;
    const height = el.offsetHeight;
    const touch = window.matchMedia('(pointer: coarse)').matches;
    const centre = rect.left + rect.width / 2;
    const left = Math.max(12, Math.min(centre - width / 2, document.documentElement.clientWidth - width - 12));
    const above = rect.top - height - 12;
    const below = touch || above < 8;
    setPosition({ left, top: below ? rect.bottom + 12 : above, below });
  }, [target]);

  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label={labels.toolbar}
      className={`z-sel-pill${position?.below ? ' z-sel-pill--below' : ''}`}
      style={position ? { left: position.left, top: position.top } : { visibility: 'hidden' }}
      onPointerDown={(event) => event.preventDefault()}
    >
      {canSpeak && (
        <>
          <button type="button" className={speaking ? 'is-on' : undefined} onClick={onListen}>
            <Volume2 aria-hidden="true" />
            {speaking ? <Wave /> : <span>{labels.listen}</span>}
          </button>
          <span className="z-sel-sep" aria-hidden="true" />
        </>
      )}
      <button type="button" onClick={onTranslate}>
        <Languages aria-hidden="true" />
        <span>{labels.translate}</span>
      </button>
    </div>
  );
}

function TranslationSheet({
  target,
  canSpeak,
  speakingRate,
  onSpeak,
  onClose,
}: {
  target: Target;
  canSpeak: boolean;
  speakingRate: number | null;
  onSpeak: (rate: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('p2e');
  const closeRef = useRef<HTMLButtonElement>(null);
  const query = trpc.lessonHelp.translate.useQuery(
    { text: target.text, context: target.context },
    { staleTime: Infinity, gcTime: 30 * 60_000, retry: 1, refetchOnWindowFocus: false },
  );
  const result = query.data;

  useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <>
      <div className="z-sel-scrim" onClick={onClose} aria-hidden="true" />
      <div className="z-sel-sheet" role="dialog" aria-modal="false" aria-label={t('selectionHelp.dialogLabel')}>
        <div className="z-sel-grab" aria-hidden="true" />
        <button ref={closeRef} type="button" className="z-sel-close" onClick={onClose} aria-label={t('selectionHelp.close')}>
          <X aria-hidden="true" />
        </button>
        <div className="z-sel-kicker">{t('selectionHelp.fromTo')}</div>
        <div className="z-sel-src">{target.text}</div>
        {result?.kind === 'word' && (result.ipa || result.partOfSpeech) && (
          <div className="z-sel-meta">
            {result.ipa && <span className="z-sel-ipa">{result.ipa}</span>}
            {result.partOfSpeech && <span className="z-sel-pos">{result.partOfSpeech}</span>}
          </div>
        )}
        <div aria-live="polite">
          {query.isLoading && (
            <div className="z-sel-loading" aria-label={t('selectionHelp.loading')}>
              <span />
              <span />
            </div>
          )}
          {query.isError && <p className="z-sel-error">{t('selectionHelp.error')}</p>}
          {result && <p className="z-sel-pt">{result.translation}</p>}
          {result?.example && (
            <p className="z-sel-ex">
              <span lang="en">{result.example.en}</span>
              <br />
              <span>{result.example.pt}</span>
            </p>
          )}
        </div>
        {canSpeak && (
          <div className="z-sel-actions">
            <button
              type="button"
              className={`z-sel-btn z-sel-btn--primary${speakingRate === 0.95 ? ' is-playing' : ''}`}
              onClick={() => onSpeak(0.95)}
            >
              <Volume2 aria-hidden="true" />
              {speakingRate === 0.95 ? <Wave /> : <span>{t('selectionHelp.listen')}</span>}
            </button>
            <button
              type="button"
              className={`z-sel-btn${speakingRate === SLOW_RATE ? ' is-playing' : ''}`}
              onClick={() => onSpeak(SLOW_RATE)}
            >
              <Snail aria-hidden="true" />
              {speakingRate === SLOW_RATE ? <Wave /> : <span>{t('selectionHelp.slow')}</span>}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function Wave() {
  return (
    <span className="z-sel-wave" aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

// ── Selection helpers ──────────────────────────────────────────────────────

function selectionTarget(container: HTMLElement): Target | null {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || !selection.rangeCount) return null;
  const range = selection.getRangeAt(0);
  const start = elementOf(range.startContainer);
  const end = elementOf(range.endContainer);
  if (!start || !end || !container.contains(start) || !container.contains(end)) return null;
  if (start.closest(NO_SELECTION_TARGETS) || end.closest(NO_SELECTION_TARGETS)) return null;
  const text = selection.toString().replace(/\s+/g, ' ').trim();
  if (!text || text.length > MAX_SELECTION_CHARS || !/[A-Za-z]/.test(text)) return null;
  return { text, context: sentenceAround(range), rect: range.getBoundingClientRect() };
}

function wordTargetAt(x: number, y: number, container: HTMLElement): Target | null {
  const range = caretRangeAt(x, y);
  if (!range || range.startContainer.nodeType !== Node.TEXT_NODE) return null;
  const node = range.startContainer as Text;
  if (!container.contains(node)) return null;
  const value = node.textContent ?? '';
  let start = range.startOffset;
  let end = range.startOffset;
  while (start > 0 && WORD_CHAR.test(value[start - 1])) start--;
  while (end < value.length && WORD_CHAR.test(value[end])) end++;
  while (end > start && /[’'-]/.test(value[end - 1])) end--;
  if (end - start < 2) return null;

  const word = document.createRange();
  word.setStart(node, start);
  word.setEnd(node, end);
  // The tapped word must actually be under the pointer (not the nearest one).
  const rect = word.getBoundingClientRect();
  if (x < rect.left - 2 || x > rect.right + 2 || y < rect.top - 2 || y > rect.bottom + 2) return null;

  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(word);
  return { text: value.slice(start, end), context: sentenceAround(word), rect };
}

function caretRangeAt(x: number, y: number): Range | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  if (doc.caretPositionFromPoint) {
    const position = doc.caretPositionFromPoint(x, y);
    if (!position) return null;
    const range = document.createRange();
    range.setStart(position.offsetNode, position.offset);
    return range;
  }
  return document.caretRangeFromPoint?.(x, y) ?? null;
}

function elementOf(node: Node): Element | null {
  return node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
}

const SENTENCE_BLOCKS = 'p, li, h1, h2, h3, h4, h5, h6, blockquote, figcaption, td, th, label, button, legend';

/** The sentence around a range, from its paragraph / list item / heading / option. */
export function sentenceAround(range: Range): string {
  const start = elementOf(range.startContainer);
  const block = start?.closest(SENTENCE_BLOCKS) ?? start;
  if (!block) return '';
  const full = (block.textContent ?? '').replace(/\s+/g, ' ').trim();
  const prefix = document.createRange();
  prefix.selectNodeContents(block);
  prefix.setEnd(range.startContainer, range.startOffset);
  const offset = prefix.toString().replace(/\s+/g, ' ').trimStart().length;
  return sentenceAt(full, offset);
}

/** The sentence of `text` that contains character `offset`. */
export function sentenceAt(text: string, offset: number): string {
  const sentences = text.match(/[^.!?]+(?:[.!?]+["”’)]*|$)\s*/g) ?? [text];
  let seen = 0;
  for (const sentence of sentences) {
    seen += sentence.length;
    if (offset < seen) return sentence.trim();
  }
  return (sentences[sentences.length - 1] ?? text).trim();
}

// ── Speech (browser voice) ─────────────────────────────────────────────────

const PREFERRED_VOICES = ['Samantha', 'Google US English', 'Microsoft Aria', 'Microsoft Jenny', 'Ava'];

function useEnglishVoice(): SpeechSynthesisVoice | null {
  const [voice, setVoice] = useState<SpeechSynthesisVoice | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const pick = () => {
      const voices = window.speechSynthesis.getVoices();
      const preferred = PREFERRED_VOICES.map((name) => voices.find((v) => v.name.includes(name))).find(Boolean);
      setVoice(
        preferred ??
          voices.find((v) => v.lang === 'en-US') ??
          voices.find((v) => v.lang.toLowerCase().startsWith('en')) ??
          null,
      );
    };
    pick();
    window.speechSynthesis.addEventListener('voiceschanged', pick);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', pick);
  }, []);
  return voice;
}

function useSpeech(voice: SpeechSynthesisVoice | null) {
  const [speaking, setSpeaking] = useState<number | null>(null);

  const stop = useCallback(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
    setSpeaking(null);
  }, []);

  const speak = useCallback(
    (text: string, rate: number) => {
      if (!voice || !window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.voice = voice;
      utterance.lang = voice.lang;
      utterance.rate = rate;
      utterance.onend = () => setSpeaking(null);
      utterance.onerror = () => setSpeaking(null);
      setSpeaking(rate);
      window.speechSynthesis.speak(utterance);
    },
    [voice],
  );

  useEffect(() => stop, [stop]);
  return { speak, stop, speaking };
}

const SELECTION_HELPER_CSS = `
.z-sel-root {
  --z-paper: #FFF8F5; --z-ink: #1A1018; --z-mid: #6B4F55; --z-red: #D41920; --z-sizzle: #E84B5A;
  --z-charm: #F2A0C4; --z-baby: #F8D7DD; --z-line: #F0DAD5;
  --z-font-cond: 'Oswald', 'Avenir Next Condensed', 'Arial Narrow', 'Helvetica Neue', sans-serif;
  --z-font-serif: 'DM Serif Text', Georgia, 'Times New Roman', serif;
  --z-font-ui: 'Montserrat', 'Avenir Next', 'Segoe UI', system-ui, sans-serif;
}
.z-sel-pill {
  position: fixed; z-index: 1000; display: flex; align-items: center; gap: 2px; padding: 4px;
  background: var(--z-ink); border-radius: 999px; box-shadow: 0 10px 24px -10px rgba(26,16,24,.6);
  animation: z-sel-pop .16s ease-out;
}
.z-sel-pill::after {
  content: ''; position: absolute; left: 50%; bottom: -6px; width: 12px; height: 12px;
  background: var(--z-ink); transform: translateX(-50%) rotate(45deg); border-radius: 2px;
}
.z-sel-pill--below::after { bottom: auto; top: -6px; }
.z-sel-pill button {
  position: relative; z-index: 1; display: inline-flex; align-items: center; gap: .45rem; border: 0;
  background: transparent; color: var(--z-paper); font: 600 .8rem/1 var(--z-font-ui);
  padding: .7rem .95rem; min-height: 40px; border-radius: 999px; cursor: pointer; white-space: nowrap;
}
.z-sel-pill button:hover { background: rgba(255,248,245,.12); }
.z-sel-pill button.is-on { background: var(--z-charm); color: var(--z-ink); }
.z-sel-pill svg { width: 16px; height: 16px; flex: none; }
.z-sel-sep { width: 1px; height: 18px; background: rgba(255,248,245,.22); }

.z-sel-scrim { position: fixed; inset: 0; z-index: 1000; background: rgba(26,16,24,.22); animation: z-sel-fade .2s; }
.z-sel-sheet {
  position: fixed; z-index: 1001; left: 50%; bottom: 0; transform: translateX(-50%);
  width: min(560px, 100%); max-height: 70vh; overflow-y: auto; background: #fff; color: var(--z-ink);
  border-top: 3px solid var(--z-ink); border-radius: 18px 18px 0 0;
  padding: .6rem max(20px, env(safe-area-inset-right, 0px)) calc(1.4rem + env(safe-area-inset-bottom, 0px)) max(20px, env(safe-area-inset-left, 0px));
  box-shadow: 0 -16px 40px -20px rgba(26,16,24,.5); font-family: var(--z-font-ui);
  animation: z-sel-rise .24s cubic-bezier(.2,.8,.2,1);
}
.z-sel-grab { width: 42px; height: 4px; border-radius: 4px; background: var(--z-line); margin: 0 auto .9rem; }
.z-sel-close {
  position: absolute; top: .6rem; right: .7rem; width: 36px; height: 36px; border: 0; border-radius: 50%;
  background: transparent; color: var(--z-mid); cursor: pointer; display: grid; place-items: center;
}
.z-sel-close:hover { background: var(--z-baby); color: var(--z-ink); }
.z-sel-close svg { width: 18px; height: 18px; }
.z-sel-kicker {
  font: 600 .66rem var(--z-font-cond); letter-spacing: .24em; text-transform: uppercase;
  color: var(--z-sizzle); margin-bottom: .45rem;
}
.z-sel-src { font-weight: 700; font-size: 1.05rem; line-height: 1.4; padding-right: 2.4rem; }
.z-sel-meta { display: flex; flex-wrap: wrap; gap: .3rem .7rem; align-items: baseline; margin-top: .25rem; font-size: .85rem; color: var(--z-mid); }
.z-sel-pos { font: 600 .66rem var(--z-font-cond); letter-spacing: .16em; text-transform: uppercase; color: var(--z-sizzle); }
.z-sel-pt { font-family: var(--z-font-serif); font-size: 1.55rem; line-height: 1.25; margin: .7rem 0 0; }
.z-sel-ex { font-size: .88rem; color: var(--z-mid); margin: .6rem 0 0; line-height: 1.5; }
.z-sel-ex span:first-child { color: var(--z-ink); font-weight: 600; }
.z-sel-error { font-size: .92rem; color: var(--z-mid); margin: .7rem 0 0; }
.z-sel-loading { display: grid; gap: .45rem; margin-top: .85rem; }
.z-sel-loading span {
  display: block; height: 1.1rem; border-radius: 4px; width: 70%;
  background: linear-gradient(90deg, var(--z-baby) 0%, #fff 50%, var(--z-baby) 100%); background-size: 200% 100%;
  animation: z-sel-shimmer 1.1s linear infinite;
}
.z-sel-loading span + span { width: 45%; }
.z-sel-actions { display: flex; flex-wrap: wrap; gap: .5rem; margin-top: 1.1rem; }
.z-sel-btn {
  display: inline-flex; align-items: center; gap: .45rem; min-height: 44px;
  font: 600 .74rem/1 var(--z-font-cond); letter-spacing: .1em; text-transform: uppercase;
  padding: .6rem .9rem; border: 1.5px solid var(--z-ink); background: #fff; color: var(--z-ink); cursor: pointer;
}
.z-sel-btn svg { width: 15px; height: 15px; }
.z-sel-btn--primary { background: var(--z-charm); box-shadow: 3px 3px 0 var(--z-ink); }
.z-sel-btn.is-playing { background: var(--z-baby); }
.z-sel-pill button:focus-visible, .z-sel-btn:focus-visible, .z-sel-close:focus-visible { outline: 2px solid var(--z-red); outline-offset: 2px; }
.z-sel-wave { display: inline-flex; gap: 2px; align-items: center; height: 14px; }
.z-sel-wave i { width: 3px; background: currentColor; border-radius: 2px; animation: z-sel-wave .8s ease-in-out infinite; }
.z-sel-wave i:nth-child(2) { animation-delay: .15s; }
.z-sel-wave i:nth-child(3) { animation-delay: .3s; }
@keyframes z-sel-pop { from { opacity: 0; transform: translateY(4px) scale(.96); } }
@keyframes z-sel-fade { from { opacity: 0; } }
@keyframes z-sel-rise { from { transform: translate(-50%, 100%); } }
@keyframes z-sel-shimmer { to { background-position: -200% 0; } }
@keyframes z-sel-wave { 0%, 100% { height: 4px; } 50% { height: 14px; } }
@media (prefers-reduced-motion: reduce) {
  .z-sel-pill, .z-sel-scrim, .z-sel-sheet, .z-sel-loading span, .z-sel-wave i { animation: none; }
}
`;
